import { useAccount, useDisconnect, useChainId, useSwitchChain } from 'wagmi';
import { LogOut, ExternalLink, Copy, Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { galactica, galacticaCassiopeia } from '@/shared/config';

const chains = [galactica, galacticaCassiopeia];

function truncateAddress(address: string) {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function Header() {
  const { address } = useAccount();
  const chainId = useChainId();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitching } = useSwitchChain();
  const [copied, setCopied] = useState(false);

  const currentChain = chains.find(c => c.id === chainId) ?? galactica;
  const explorerUrl = currentChain.blockExplorers?.default.url;

  const copyAddress = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      <div>
        {/* Breadcrumb or page title could go here */}
      </div>

      <div className="flex items-center gap-4">
        {/* Network switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 px-3 py-1.5 h-auto bg-[#f8f4eb] hover:bg-[#f0ebe0]">
              <div className={`w-2 h-2 rounded-full ${currentChain.testnet ? 'bg-yellow-500' : 'bg-green-500'}`} />
              <span className="text-sm text-[#2b2b2b]">
                {isSwitching ? 'Switching...' : currentChain.name}
              </span>
              <ChevronDown className="w-4 h-4 text-[#807f7e]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {chains.map((chain) => (
              <DropdownMenuItem
                key={chain.id}
                onClick={() => switchChain({ chainId: chain.id })}
                className="gap-2"
                disabled={chain.id === chainId}
              >
                <div className={`w-2 h-2 rounded-full ${chain.testnet ? 'bg-yellow-500' : 'bg-green-500'}`} />
                {chain.name}
                {chain.testnet && <span className="text-xs text-[#807f7e]">(Testnet)</span>}
                {!chain.testnet && <span className="text-xs text-[#807f7e]">(Mainnet)</span>}
                {chain.id === chainId && <Check className="w-4 h-4 ml-auto" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Wallet dropdown */}
        {address && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#f26f56] to-[#f49756]" />
                <span className="font-mono">{truncateAddress(address)}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={copyAddress} className="gap-2">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Address'}
              </DropdownMenuItem>
              {explorerUrl && (
                <DropdownMenuItem asChild>
                  <a
                    href={`${explorerUrl}/address/${address}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View on Explorer
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => disconnect()}
                className="gap-2 text-red-600 focus:text-red-600"
              >
                <LogOut className="w-4 h-4" />
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
